'use client'
import React, { useEffect, useState } from 'react';
import {
    onAuthStateChanged,
    getAuth,
} from 'firebase/auth';
import {
    getDoc,
    doc,
    // DocumentReference,
} from 'firebase/firestore'
import firebase_app from '@/app/firebase/config';
import { LoadingDialog} from '@/components/loading-dialog';
import { getFirestoreInstance } from '@/lib/firestore/firebase-singleton';
import logger from '@/components/logger';

const auth = getAuth(firebase_app);

// Initialize Canny.io script
if (typeof window !== 'undefined') {
    (function(w,d,i,s){function l(){if(!d.getElementById(i)){var f=d.getElementsByTagName(s)[0],e=d.createElement(s);e.type="text/javascript",e.async=!0,e.src="https://canny.io/sdk.js",f.parentNode.insertBefore(e,f)}}if("function"!=typeof w.Canny){var c=function(){c.q.push(arguments)};c.q=[],w.Canny=c,"complete"===d.readyState?l():w.attachEvent?w.attachEvent("onload",l):w.addEventListener("load",l,!1)}})(window,document,"canny-jssdk","script");
}

const initialUserState = {
    uid: null,
    user_type: { updated_time: 0, type: 'normal' },
    idToken: '',
    displayName: '',
    email: '',
    photoURL: '',
    stsTokenManager: {
        accessToken: '',
        refreshToken: ''
    },
    credits: {
        available: 0,
    },
    crawl_limit: {
        free_ai_overview: 0,
        ai_overview: 0,
        site_audit: 1500,
        free_site_audit: 10,
        competitor_pages: 0,
        competitor_sites: 0
    }
};

export const AuthContext = React.createContext({
    user: initialUserState,
    setUpdateUser: () => {},
    loading: true,
    authStatus: 'loading'
});

export const useAuthContext = () => React.useContext(AuthContext);

const getCurrentUser = async(uid) => {
    try {
        if (!uid) return null;
        
        const db = getFirestoreInstance();
        const docRef = doc(db, 'users', uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            return docSnap.data();
        }
        // New signups can hit this path before the `users/{uid}` doc is created.
        return null;
    } catch(err) {
        logger.error(err instanceof Error ? err : new Error(String(err)), 'Error getting current user', [], undefined);
        throw err;
    }
};

export const AuthContextProvider = ({ children }) => {
    const [user, setUser] = useState(initialUserState);
    const [updateUser, setUpdateUser] = useState(true);
    const [loading, setLoading] = useState(true);
    const [authStatus, setAuthStatus] = useState('loading');

    const handleSetLoading = (loading_state) => {
        setLoading(loading_state);
        setUpdateUser(loading_state);
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            // logger.info('auth state changed', auth);
            // logger.info('user onAuthStateChanged', [{email: user?.email}], user?.uid);

            if (user && user.uid) {
                try {
                    const idToken = await user.getIdToken();
                    if (!idToken) {
                        setUser(initialUserState);
                        // setAuthStatus('unauthorised');
                        handleSetLoading(false);
                        return;
                    }

                    // Authorisation check:
                    // - authorised if users/{uid} exists
                    // - or if authorised_pending_users/{emailLower} exists, promote to users/{uid}
                    let user_doc = await getCurrentUser(user.uid);
                    console.log('user_doc', user_doc);
                    setAuthStatus('authorised');


                    if (user_doc && user_doc.user_type) {
                        const { user_type, saved, credits } = user_doc;
                        const new_saved = saved && saved.length ? saved : [];
                        const new_credits = credits && credits.available ? credits.available : 0;
                        const crawl_limit = user_doc?.crawl_limit ?? initialUserState.crawl_limit;
                        const updatedUser = {
                            ...user,
                            saved: new_saved,
                            user_type,
                            idToken,
                            credits: {
                                available: new_credits
                            },
                            crawl_limit,
                        };
                        setUser(updatedUser);
                        

                        // Process pending AIO report IDs
                        if (typeof window !== 'undefined') {
                            try {
                                const pendingReportsStr = localStorage.getItem('pending_aio_report_ids');
                                if (pendingReportsStr) {
                                    const pendingReports = JSON.parse(pendingReportsStr);
                                    if (Array.isArray(pendingReports) && pendingReports.length > 0) {
                                        // console.log(`🔄 Processing ${pendingReports.length} pending AIO report(s) for user ${user.uid}`);
                                        
                                        // Import the update function (dynamic import to avoid circular dependencies)
                                        const { updateMultipleReportUserIds } = await import('@/lib/firestore/ai-overview-analysis');
                                        const result = await updateMultipleReportUserIds(pendingReports, user.uid);
                                        
                                        if (result.success.length > 0) {
                                            console.log(`✅ Successfully linked ${result.success.length} report(s) to user`);
                                        }
                                        if (result.failed.length > 0) {
                                            console.warn(`⚠️ Failed to link ${result.failed.length} report(s)`);
                                        }
                                        
                                        // Clear all pending reports (both successful and failed)
                                        // Failed ones will need manual intervention if needed
                                        localStorage.removeItem('pending_aio_report_ids');
                                    }
                                }
                            } catch (err) {
                                logger.error(err instanceof Error ? err : new Error(String(err)), 'Error processing pending AIO reports', [], undefined);
                                // Don't block auth flow if this fails
                            }

                            try {
                                const { claimReportAccessInvites } = await import('@/lib/api/aio-report-access');
                                const claimEmail = user_doc.email || user.email || '';
                                await claimReportAccessInvites(claimEmail);
                            } catch (err) {
                                logger.error(err instanceof Error ? err : new Error(String(err)), 'Error claiming AIO report access invites', [], undefined);
                            }
                        }

                        // Send user ID to Microsoft Clarity
                        if (typeof window !== 'undefined' && window.clarity && user.uid) {
                            window.clarity('identify', user.uid);
                            
                            // Check if user's email is in DISABLED_USERS and set custom parameter for filtering
                            const disabledUsers = process.env.NEXT_PUBLIC_CLARITY_DISABLED_USERS
                                ? process.env.NEXT_PUBLIC_CLARITY_DISABLED_USERS.split(',').map(u => u.trim())
                                : [];
                            
                            const userEmail = user_doc.email || user.email;
                            if (userEmail && disabledUsers.includes(userEmail)) {
                                window.clarity('set', 'is_disabled_user', 'true');
                            }
                        }
                    } else {
                        // Signed in but not authorised; keep minimal identity for UI messaging (login warning).
                        setUser({
                            ...initialUserState,
                            uid: user.uid,
                            idToken,
                            displayName: user?.displayName ?? '',
                            email: user?.email ?? '',
                            photoURL: user?.photoURL ?? ''
                        });
                        // setAuthStatus('unauthorised');
                    }
                } catch (err) {
                    logger.error(err instanceof Error ? err : new Error(String(err)), 'Error in auth state change', [], undefined);
                    setUser(initialUserState);
                    // setAuthStatus('unauthorised');
                }
            } else {
                setUser(initialUserState);
                setAuthStatus('unauthorised');
            }
            handleSetLoading(false);
        });

        return () => unsubscribe();
    }, [updateUser]);

    // console.log('authStatus', authStatus);
    // console.log('loading', loading);

    return (
        <AuthContext.Provider value={{ user, setUpdateUser, loading, authStatus }}>
            {/* Always render children - loading dialog is an overlay, not a replacement */}
            {children}
            {/* Show loading dialog as overlay when loading (only when JS is available) */}
            {loading && typeof window !== 'undefined' && (
                <LoadingDialog
                    open={loading}
                    duration={5}
                    onClose={() => {}}
                />
            )}
        </AuthContext.Provider>
    );
};